angular.module('galilai.creator').controller('MediaStudioController', function ($scope, api, $timeout, $rootScope) {
    // How often we ask whether the image is ready. A generation takes 10 to 40 seconds,
    // so 2s is the same rhythm as the post editor.
    const POLL_INTERVAL = 2000;
    // 'original' preserves the source image's framing and only exists when modifying
    const ASPECTS = ['1:1', '4:5', '9:16', '16:9'];
    const ORIGINAL = 'original';

    const BASE_STATE = {
        // 'create' | 'modify' — the only difference is having a source image
        mode: 'create',
        source: null,
        projectId: null,
        collection: null,
        prompt: '',
        tier: null,
        aspect: '1:1',
    };
    const BASE_VIEW = {
        // 'form' | 'generating' | 'done'
        step: 'form',
        credits: null,
        error: null,
        media: null,
        jobId: null,
        zoom: false,
    };

    $scope.aspects = ASPECTS;
    $scope.tiers = [];
    $scope.data = angular.copy(BASE_STATE);
    $scope.view = angular.copy(BASE_VIEW);

    let pollTimer = null;

    function stopPolling() {
        if (pollTimer) {
            $timeout.cancel(pollTimer);
            pollTimer = null;
        }
    }

    function reset() {
        stopPolling();
        $scope.data = angular.copy(BASE_STATE);
        $scope.view = angular.copy(BASE_VIEW);
    }

    // Tiers come from the server with id and cost. Which provider serves each one is
    // deliberately not sent down here.
    function loadTiers() {
        api.getImageTiers().then(function (res) {
            $scope.tiers = res?.data?.tiers || [];
            if (!$scope.data.tier) {
                const preferred = $scope.tiers.filter(function (t) { return t.isDefault; })[0];
                $scope.data.tier = (preferred || $scope.tiers[0] || {}).id || null;
            }
            $timeout(angular.noop);
        }).catch(function () {
            $scope.tiers = [];
        });
    }

    function refreshCredits() {
        api.getCurrentCredits().then(function (res) {
            $scope.view.credits = res.data.credits;
            $timeout(angular.noop);
        }).catch(function () {
            // Without a balance on screen the cost still shows; not worth blocking for
        });
    }

    // The studio opens on top of another modal (the media picker or the lookalike one).
    // Pushing the one below back is what keeps the two from competing for attention.
    function dimModalsBehind(dim) {
        const modals = document.querySelectorAll('.modal.show');
        modals.forEach(function (modal) {
            if (modal.id === 'media-studio-modal') {
                return;
            }
            modal.classList.toggle('is-dimmed', dim);
        });
    }

    $rootScope.$on('media.studio.show', function (event, data) {
        reset();
        $scope.data.mode = data.mode === 'modify' ? 'modify' : 'create';
        $scope.data.source = data.source || null;
        $scope.data.projectId = data.projectId || null;
        $scope.data.collection = data.collection || null;
        // Who opened the studio, so the finished image goes back to the right place
        $scope.data.origin = data.origin || null;
        // Modifying keeps the original's framing by default: changing the ratio reframes
        // the image, and asking to modify is not asking to crop.
        $scope.data.aspect = $scope.data.mode === 'modify' ? ORIGINAL : '1:1';

        loadTiers();
        refreshCredits();

        const element = angular.element('#media-studio-modal');
        element.modal('show');
        dimModalsBehind(true);
        element.off('hidden.bs.modal.studio').on('hidden.bs.modal.studio', function () {
            dimModalsBehind(false);
        });
    });

    $scope.isModify = function () {
        return $scope.data.mode === 'modify';
    };

    $scope.promptPlaceholder = function () {
        if (!$scope.isModify()) {
            return gettext('mediastudio.placeholder_create');
        }
        return $scope.data.origin === 'avatarpicker'
            ? gettext('mediastudio.placeholder_avatar')
            : gettext('mediastudio.placeholder_modify');
    };

    $scope.tierLabel = function (tier) {
        return gettext('mediastudio.tier_' + tier.id);
    };

    $scope.tierHint = function (tier) {
        return gettext('mediastudio.tier_' + tier.id + '_hint');
    };

    $scope.chooseTier = function (tierId) {
        $scope.data.tier = tierId;
    };

    $scope.chooseAspect = function (aspect) {
        $scope.data.aspect = aspect;
    };

    $scope.aspectOptions = function () {
        return $scope.isModify() ? [ORIGINAL].concat(ASPECTS) : ASPECTS;
    };

    $scope.aspectLabel = function (aspect) {
        return aspect === ORIGINAL ? gettext('mediastudio.aspect_original') : aspect;
    };

    /**
     * The suggestions are writing shortcuts: they append to what is already there instead
     * of replacing it, or the user would lose what they typed by clicking one.
     *
     * There are three sets because what makes a good suggestion changes with what you are
     * doing: when creating, the tag is a *modifier* (the user writes the subject and the
     * tag gives the style); when modifying, the tag is the *whole instruction*, so it has
     * to stand alone — "change the background" makes the model guess to what, "white
     * background" does not.
     */
    $scope.chips = function () {
        let key = 'mediastudio.chips_create';
        if ($scope.isModify()) {
            key = $scope.data.origin === 'avatarpicker'
                ? 'mediastudio.chips_avatar'
                : 'mediastudio.chips_modify';
        }
        return (gettext(key) || '').split('|').filter(Boolean);
    };

    $scope.addChip = function (chip) {
        const current = ($scope.data.prompt || '').trim();
        $scope.data.prompt = current ? current + ', ' + chip : chip;
    };

    $scope.selectedTier = function () {
        return $scope.tiers.filter(function (t) { return t.id === $scope.data.tier; })[0] || null;
    };

    $scope.getCredits = function () {
        const tier = $scope.selectedTier();
        return tier ? tier.credits : 0;
    };

    $scope.canGenerate = function () {
        if ($scope.view.step === 'generating') {
            return false;
        }
        if ($scope.isModify() && !$scope.data.source) {
            return false;
        }
        return Boolean(($scope.data.prompt || '').trim()) && Boolean($scope.data.tier);
    };

    $scope.generate = function () {
        if (!$scope.canGenerate()) {
            return;
        }
        $scope.view.step = 'generating';
        $scope.view.error = null;
        $scope.view.media = null;

        api.generateMedia({
            prompt: $scope.data.prompt,
            tier: $scope.data.tier,
            aspect: $scope.data.aspect,
            projectId: $scope.data.projectId,
            collection: $scope.data.collection,
            sourceMediaId: $scope.data.source ? $scope.data.source._id : null,
        }).then(function (res) {
            if (!res?.data?.success) {
                failed(res?.data?.error);
                return;
            }
            $scope.view.jobId = res.data.jobId;
            poll();
        }).catch(function () {
            failed('generic');
        });
    };

    function failed(code) {
        stopPolling();
        $scope.view.step = 'form';
        if (code === 'no_credits') {
            $rootScope.$emit('credits.show');
            return;
        }
        const key = 'mediastudio.error_' + code;
        $scope.view.error = gettext(key) || gettext('error.generic');
        $timeout(angular.noop);
    }

    function poll() {
        pollTimer = $timeout(function () {
            api.getMediaGeneration($scope.view.jobId).then(function (res) {
                const status = res?.data?.status;
                if (status === 'success') {
                    stopPolling();
                    $scope.view.media = res.data.media;
                    $scope.view.step = 'done';
                    // O seletor escuta isto e recarrega a biblioteca sozinho
                    $rootScope.$broadcast('media.item.created', { kind: 'image' });
                    refreshCredits();
                    return;
                }
                if (status === 'error') {
                    failed(res.data.error);
                    return;
                }
                poll();
            }).catch(function () {
                failed('generic');
            });
        }, POLL_INTERVAL);
    }

    // Generating another from the same prompt: whoever asks for another version almost
    // always wants to tweak the text, not start over.
    $scope.generateAnother = function () {
        $scope.view.step = 'form';
        $scope.view.media = null;
        $scope.view.error = null;
    };

    $scope.useImage = function () {
        if (!$scope.view.media) {
            return;
        }
        const media = $scope.view.media;
        angular.element('#media-studio-modal').modal('hide');
        $rootScope.$broadcast('media.studio.use', {
            _id: media._id,
            data: media,
            origin: $scope.data.origin,
        });
    };

    // An overlay of its own instead of a second modal: Bootstrap does not handle a modal
    // inside a modal well, and this way the viewer stays in this controller's scope.
    $scope.openFullscreen = function () {
        if (!$scope.view.media) {
            return;
        }
        $scope.view.zoom = true;
    };

    $scope.closeFullscreen = function () {
        $scope.view.zoom = false;
    };

    // Closing midway cancels nothing: the job goes on in the server and the image lands
    // in the library either way. We just stop asking.
    angular.element('#media-studio-modal').on('hidden.bs.modal', function () {
        stopPolling();
    });
});
